import { Button, Grid, styled, CircularProgress } from "@mui/material";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import AppTextField from "components/input-fields/AppTextField";
import Scrollbar from "components/ScrollBar";
import { H2, H6 } from "components/Typography";
import { useState, useEffect } from "react"; // Import useState and useEffect
import toast from "react-hot-toast";
import { API_ENDPOINTS } from "Network/apiEndpoints"; // Import API_ENDPOINTS
import apiService from "Network/apiService"; // Import apiService
import * as Yup from "yup";
// import Select from "@mui/material/Select"; //Comment out business category
// import MenuItem from "@mui/material/MenuItem";  //Comment out business category
// import FormControl from "@mui/material/FormControl";  //Comment out business category
// import OutlinedInput from "@mui/material/OutlinedInput";  //Comment out business category
import InputAdornment from '@mui/material/InputAdornment'; 
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility'; 
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Typography from "@mui/material/Typography";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { Add, DeleteOutline } from '@mui/icons-material';

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 450,
  minWidth: 200,
  [theme.breakpoints.down(325)]: {
    maxWidth: "100%",
  },
}))

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}
const AddChapterModal = ({ open, onClose, edit, data, region, onSuccess }) => {
  const toast = useToast();
  // Initialize state variables
  const [chapterName, setChapterName] = useState("");
  const [description, setDescription] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [emails, setEmails] = useState(['']); // Initialize with one empty email field
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (edit && data) {
      setIsEdit(true);
      setChapterName(data.chapter_name || "");
      setDescription(data.description || "");
      setUsername(data.username || "");
      // Set emails if they exist, otherwise initialize with one empty field
      setEmails(data.emails ? data.emails.split(',') : ['']);
      setPassword("");
    } else {
      setIsEdit(false);
      setChapterName("");
      setDescription("");
      setUsername("");
      setPassword("");
      setEmails(['']); // Reset to one empty email field
    }
  }, [edit, data]);

  const fieldValidationSchema = Yup.object().shape({
    chapterName: Yup.string().required("Chapter Name is Required!"),
    description: Yup.string(),
    username: Yup.string().required("Username is Required!"),
    // Make password only required for new chapters
    password: Yup.string().when('$isEdit', {
        is: true,
        then: () => Yup.string(), // Optional in edit mode
        otherwise: () => Yup.string().required("Password is Required!")
    }),
    // Add validation for emails array
    emails: Yup.array()
        .min(1, "At least one notification email is required")
        .of(
            Yup.string()
                .email("Invalid email format")
                .required("Email is required")
        )
  })

  // Add email field
  const handleAddEmail = () => {
    if (emails.length < 3) {
      setEmails([...emails, '']);
    }
  };

  // Remove email field
  const handleRemoveEmail = (index) => {
    const newEmails = emails.filter((_, i) => i !== index);
    setEmails(newEmails);
  };

  // Update email value
  const handleEmailChange = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    setIsSubmitting(true);

    try {
        // Filter out empty emails
        const filteredEmails = emails.filter(email => email.trim());
        
        // Check for minimum email requirement before validation
        if (filteredEmails.length === 0) {
            setValidationErrors({ emails: "At least one notification email is required" });
            toast.showError("At least one notification email is required");
            setIsSubmitting(false);
            return;
        }

        await fieldValidationSchema.validate({
            chapterName,
            description,
            username,
            password,
            emails: filteredEmails,
        }, { 
            abortEarly: false,
            context: { isEdit: edit }
        });

        const payload = {
            user: localStorage.getItem("user_id"),
            region: region,
            name: chapterName,
            description: description,
        };

        if (!isEdit || (isEdit && password)) {
            const filteredEmails = emails.filter(email => email.trim());
            if (filteredEmails.length > 0) {
                payload.emails = filteredEmails.join(',');
            }
        }

        if (password) {
            payload.password = password;
        }

        let response;
        if (isEdit) {
            payload.chapter_id = data.id;
            response = await apiService.put(API_ENDPOINTS.PUT_update_chapter, payload);
        } else {
            payload.username = username;
            payload.password = password;
            response = await apiService.post(API_ENDPOINTS.POST_create_chapters, payload);
        }

        if (response?.success) {
            toast.showSuccess(isEdit ? "Chapter updated!" : "Chapter created!");
            onClose();
            onSuccess?.();
        } else {
            toast.showError(response?.message || "Operation failed");
        }
    } catch (error) {
        if (error instanceof Yup.ValidationError) {
            const errors = {};
            error.inner.forEach(err => {
                if (err.path === 'emails') {
                    errors.emails = "At least one valid notification email is required";
                } else {
                    errors[err.path] = err.message;
                }
            });
            setValidationErrors(errors);
            toast.showError(errors.emails || "Please check the form for errors");
        } else {
            console.error("API error:", error);
            toast.showError("An unexpected error occurred");
        }
    } finally {
        setIsSubmitting(false);
    }
  };

  return (
    <StyledAppModal open={open} handleClose={onClose}>
      <H2 mb={2}>{edit ? "Edit Chapter" : "Add new Chapter"}</H2>

      <form onSubmit={handleSubmit}>
        <Scrollbar style={{ maxHeight: 400 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <H6 mb={1}>Chapter Name</H6>
              <AppTextField
                fullWidth
                size="small"
                name="chapterName"
                placeholder="Enter Chapter Name"
                value={chapterName}
                onChange={(e) => setChapterName(e.target.value)}
                error={Boolean(validationErrors.chapterName)}
                helperText={validationErrors.chapterName}
              />
            </Grid>

            <Grid item xs={12}>
              <H6 mb={1}>Description</H6>
              <AppTextField
                fullWidth
                size="small"
                name="description"
                placeholder="Enter Description"
                multiline
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                error={Boolean(validationErrors.description)}
                helperText={validationErrors.description}
              />
            </Grid>

            {/*  Comment out business category */}
            {/* <Grid item xs={12}>
              <H6 mb={1}>Business Categories</H6>
              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.businessCategories && touched.businessCategories)}
              >
                <Select
                  multiple
                  value={values.businessCategories}
                  onChange={(e) => setFieldValue("businessCategories", e.target.value)}
                  input={<OutlinedInput />}
                  MenuProps={MenuProps}
                >
                  {businessCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {touched.businessCategories && errors.businessCategories && (
                  <span className="MuiFormHelperText-root Mui-error">{errors.businessCategories}</span>
                )}
              </FormControl>
            </Grid> */}

            <Grid item xs={12}>
              <H6 mb={1}>Username</H6>
              <AppTextField
                fullWidth
                size="small"
                name="username"
                placeholder="Enter Username"
                value={username}
                disabled={isEdit}
                onChange={(e) => setUsername(e.target.value)}
                error={Boolean(validationErrors.username)}
                helperText={isEdit ? "Username cannot be changed" : validationErrors.username}
              />
            </Grid>

            <Grid item xs={12}>
              <H6 mb={1}>
                Password 
                {isEdit && (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    color="info.main" 
                    sx={{ ml: 1 }}
                  >
                    (Optional - Enter only if you want to change it)
                  </Typography>
                )}
              </H6>
              <AppTextField
                fullWidth
                size="small"
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder={isEdit ? "Enter new password (optional)" : "Enter password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={Boolean(validationErrors.password)}
                helperText={validationErrors.password}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {isEdit && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ display: 'block', mt: 1 }}
                >
                  🔒 For security reasons, existing passwords cannot be viewed. 
                  Leave blank to keep the current password.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <H6 mb={1}>Notification Email Addresses (Max 3)</H6>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ display: 'block', mb: 2 }}
              >
                ℹ️ These email addresses will receive the chapter's login credentials.
                Please add only trusted individuals who need access to the chapter dashboard.
              </Typography>
              {emails.map((email, index) => (
                <FlexBox key={index} gap={1} mb={1}>
                  <AppTextField
                    fullWidth
                    size="small"
                    type="email"
                    placeholder="Enter email address"
                    value={email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                  />
                  {emails.length > 1 && (
                    <IconButton 
                      size="small" 
                      onClick={() => handleRemoveEmail(index)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  )}
                </FlexBox>
              ))}
              {emails.length < 3 && (
                <Button
                  size="small"
                  onClick={handleAddEmail}
                  startIcon={<Add />}
                  sx={{ mt: 1 }}
                >
                  Add Email
                </Button>
              )}
            </Grid>
          </Grid>
        </Scrollbar>

        <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
          <Button 
            fullWidth 
            size="small" 
            variant="outlined" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            fullWidth 
            size="small" 
            type="submit" 
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting 
              ? (isEdit ? "Updating..." : "Creating...") 
              : (isEdit ? "Update Chapter" : "Create Chapter")
            }
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  )
}

export default AddChapterModal
